import type { FiatCurrency } from '@/queries/types/FiatCurrency.types';

const FIAT_CURRENCY_PREFIXES = {
  USD: '$',
  CAD: 'C$ ',
} as const satisfies Record<FiatCurrency, string>;

export function getFiatCurrencyPrefix(currency: FiatCurrency): string {
  return FIAT_CURRENCY_PREFIXES[currency];
}

/**
 * Formats an explicit fiat amount without relying on Intl's ambiguous `$`
 * symbol for CAD. Persisted entities pass their stored currency; pre-order
 * storefront offers pass the currency derived from the authenticated market.
 */
export function formatFiatAmount(
  cents: number,
  currency: FiatCurrency,
  locale: string = 'en-US',
): string {
  const sign = cents < 0 ? '-' : '';
  const formattedAmount = formatCentsToCurrency(Math.abs(cents), locale);

  return `${sign}${getFiatCurrencyPrefix(currency)}${formattedAmount}`;
}

/**
 * Formats an amount received in minor units as a decimal string with two
 * fractional digits and thousands separators. It intentionally adds no symbol.
 *
 * The backend always provides currency values in cents (e.g., 12345 for $123.45).
 * This function converts the minor units to major units for display.
 *
 * @param cents - The currency amount in cents from the backend.
 * @param locale - Optional. The locale string for formatting (default is 'en-US').
 * @returns The formatted numeric string (e.g., "1,234.56").
 */
export function formatCentsToCurrency(cents: number, locale: string = 'en-US'): string {
  return (cents / 100).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatCentsToCurrencyWithDecimals(cents: number, locale: string = 'en-US'): string {
  return (cents / 100).toLocaleString(locale);
}

export function formatCentsToCurrencyWithoutDecimal(cents: number, locale: string = 'en-US'): string {
  return (cents / 100).toLocaleString(locale, { maximumFractionDigits: 0 });
}

export function formatCentsToCurrencyNumber(cents: number): number {
  return (cents / 100);
}

export function formatCurrencyToCents(amount: number): number {
  return Math.round(amount * 100);
}

/**
 * Formats an amount like `formatCentsToCurrency`, but wraps each digit in a fixed-width
 * `<span>` so a value's on-screen width stays stable as digits change (e.g. during a
 * count-up animation) without relying on `tabular-nums`, which swaps in the font's tnum
 * glyph set and can drift from the design's proportional figures.
 *
 * Returns an HTML string — intended for `dangerouslySetInnerHTML` or libraries (like
 * countup.js) that assign the result via `innerHTML`.
 */
export function formatCurrencyWithStableDigitWidth(amount: number, locale: string = 'en-US'): string {
  const formatted = amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return formatted.replace(/\d/g, digit => `<span class="inline-block w-[1ch] text-center">${digit}</span>`);
}

/**
 * Formats a game multiplier for display, rounding to a fixed number of decimals
 * and trimming trailing zeros so long floating-point values don't overflow the UI.
 * - 0.35714285714285715 -> "0.36"
 * - 2 -> "2"
 * - 1.5 -> "1.5"
 *
 * @param multiplier - The raw multiplier value from the backend.
 * @param decimals - Optional. Maximum number of decimal places (default is 2).
 * @returns The formatted multiplier string.
 */
export function formatMultiplier(multiplier: number, decimals: number = 2): string {
  if (!Number.isFinite(multiplier)) {
    return '0';
  }

  const factor = 10 ** Math.max(0, decimals);
  const rounded = Math.round(multiplier * factor) / factor;

  return decimals > 0
    ? rounded.toFixed(decimals).replace(/0+$/, '').replace(/\.$/, '')
    : String(rounded);
}

/**
 * Removes zero-only decimal parts from every number inside a string,
 * keeping meaningful decimals as-is.
 * - "50.00 SC extra" -> "50 SC extra"
 * - "14.99 SC extra" -> "14.99 SC extra"
 *
 * @param text - Any text that may contain numbers.
 * @returns The text with `.00`-style decimals stripped.
 */
export function trimZeroDecimals(text: string): string {
  return text.replace(/(\d)\.0+(?!\d)/g, '$1');
}

type FormatCompactNumberOptions = {
  scale?: number;
  suffixes?: {
    thousand: string;
    million: string;
    billion: string;
  };
  decimals?: number;
  /**
   * Smallest unit to compact. Defaults to `'thousand'`.
   * When set to `'million'`, values below 1M are shown in full (with thousands
   * separators) and only millions/billions are compacted.
   */
  minUnit?: 'thousand' | 'million';
};

/**
 * Formats big numbers to compact form:
 * - 999 -> "999"
 * - 10_000 -> "10к"
 * - 10500 -> "10.5к" (with decimals=1)
 * - 1_200_000 -> "1.2м"
 *
 * With `minUnit: 'million'` thousands are not compacted:
 * - 250_000 -> "250,000"
 * - 1_200_000 -> "1.2M"
 */
export function formatCompactNumber(value: number, options: FormatCompactNumberOptions = {}): string {
  const {
    suffixes = { thousand: 'K', million: 'M', billion: 'B' },
    decimals = 1,
    scale = 1,
    minUnit = 'thousand',
  } = options;

  if (!Number.isFinite(value)) {
    return '0';
  }

  const normalized = scale !== 0 ? value / scale : value;
  const sign = normalized < 0 ? '-' : '';
  const abs = Math.abs(normalized);

  const round = (n: number) => {
    const factor = 10 ** Math.max(0, decimals);
    return Math.round(n * factor) / factor;
  };

  const format = (n: number, suffix: string) => {
    const rounded = round(n);
    const asString = decimals > 0 ? rounded.toFixed(decimals) : String(rounded);

    // Trim trailing zeros safely (no catastrophic backtracking).
    // Examples: "10.0" -> "10", "10.50" -> "10.5"
    let trimmed = asString;
    if (decimals > 0 && trimmed.includes('.')) {
      trimmed = trimmed.replace(/0+$/, '').replace(/\.$/, '');
    }

    return `${sign}${trimmed}${suffix}`;
  };

  if (abs >= 1_000_000_000) {
    return format(abs / 1_000_000_000, suffixes.billion);
  }
  if (abs >= 1_000_000) {
    return format(abs / 1_000_000, suffixes.million);
  }
  if (minUnit !== 'million' && abs >= 1_000) {
    return format(abs / 1_000, suffixes.thousand);
  }

  // Below the smallest compacted unit: show in full with thousands separators.
  // Rounding still removes float artifacts (e.g. 29.9900000002).
  return `${sign}${round(abs).toLocaleString('en-US', { maximumFractionDigits: Math.max(0, decimals) })}`;
}
